import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import {
  AVAILABILITY_OPTIONS, MOBILITY_OPTIONS, applicationStatusLabel, employmentTypeLabel,
} from "@/lib/constants";
import { MapPin, Trash2, FileText, Bookmark, Send, MessageSquare } from "lucide-react";
import DocumentsSection from "@/components/DocumentsSection";
import CandidateStats from "@/components/dashboards/CandidateStats";
import CandidateInterviews from "@/components/dashboards/CandidateInterviews";
import NexaProfileSection from "@/components/NexaProfileSection";
import VerificationsSection from "@/components/VerificationsSection";

const EMPTY_FORM = {
  full_name: "",
  profession: "",
  summary: "",
  skills: "",
  languages: "",
  experience_years: 0,
  country: "",
  city: "",
  education: "",
  certifications: "",
  availability: "immediate",
  mobility: "internationale",
  desired_countries: "",
  desired_salary: "",
  visibility: "public",
  noc_codes: "",
};

const COMPLETENESS_FIELDS = [
  "full_name", "profession", "summary", "skills", "languages",
  "experience_years", "country", "education", "availability", "desired_countries",
];

function computeCompleteness(cv) {
  if (!cv) return 0;
  const filled = COMPLETENESS_FIELDS.filter((f) => {
    const v = cv[f];
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "number") return v > 0;
    return v && String(v).trim() !== "";
  });
  return Math.round((filled.length / COMPLETENESS_FIELDS.length) * 100);
}

export default function CandidateDashboard() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [cvs, setCvs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [savedOffers, setSavedOffers] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [cvRes, appRes, savedRes] = await Promise.all([
        api.get("/cvs/mine"),
        api.get("/applications/mine"),
        api.get("/saved/mine"),
      ]);
      setCvs(cvRes.data);
      setApplications(appRes.data);
      setSavedOffers(savedRes.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      await api.post("/cvs", {
        ...form,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
        certifications: form.certifications.split(",").map((s) => s.trim()).filter(Boolean),
        desired_countries: form.desired_countries.split(",").map((s) => s.trim()).filter(Boolean),
        noc_codes: form.noc_codes.split(",").map((s) => s.trim()).filter(Boolean),
        experience_years: Number(form.experience_years) || 0,
      });
      await loadAll();
      setSuccess("CV publié avec succès.");
      setForm(EMPTY_FORM);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteCv = async (cvId) => {
    try {
      await api.delete(`/cvs/${cvId}`);
      await loadAll();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const latestCv = cvs[0];
  const completeness = computeCompleteness(latestCv);

  const inputCls =
    "w-full border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise focus:border-transparent";

  return (
    <div data-testid="candidate-dashboard" className="space-y-8">
      {/* Profile completeness */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold text-brand-navy">Complétude du profil</h2>
          <span data-testid="profile-completeness" className="text-sm font-bold text-brand-turquoise">{completeness}%</span>
        </div>
        <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-brand-blue to-brand-turquoise rounded-full transition-all duration-500"
            style={{ width: `${completeness}%` }}
          />
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Un profil complet augmente votre visibilité auprès des employeurs et recruteurs.
        </p>
      </div>

      <NexaProfileSection />

      <CandidateStats />

      <div className="grid lg:grid-cols-2 gap-8">
        {/* CV form */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="font-semibold text-brand-navy mb-5">Déposer mon CV</h2>

          {error && (
            <div data-testid="cv-form-error" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
          )}
          {success && (
            <div data-testid="cv-form-success" className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">{success}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="cv-name-input" required value={form.full_name} onChange={set("full_name")} placeholder="Nom complet *" className={inputCls} />
              <input data-testid="cv-profession-input" required value={form.profession} onChange={set("profession")} placeholder="Profession *" className={inputCls} />
            </div>
            <textarea
              data-testid="cv-summary-input"
              value={form.summary}
              onChange={set("summary")}
              placeholder="Résumé de votre parcours"
              rows={3}
              className={inputCls}
            />
            <input data-testid="cv-skills-input" value={form.skills} onChange={set("skills")} placeholder="Compétences (séparées par des virgules)" className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="cv-languages-input" value={form.languages} onChange={set("languages")} placeholder="Langues (Français, Anglais…)" className={inputCls} />
              <input data-testid="cv-education-input" value={form.education} onChange={set("education")} placeholder="Formation / diplômes" className={inputCls} />
            </div>
            <input data-testid="cv-certifications-input" value={form.certifications} onChange={set("certifications")} placeholder="Certifications (séparées par des virgules)" className={inputCls} />
            <input data-testid="cv-noc-input" value={form.noc_codes} onChange={set("noc_codes")} placeholder="Codes CNP/NOC visés (ex. 64100, 31301)" className={inputCls} />
            <div className="grid grid-cols-3 gap-3">
              <input
                data-testid="cv-experience-input"
                type="number"
                min={0}
                value={form.experience_years}
                onChange={set("experience_years")}
                placeholder="Années d'exp."
                className={inputCls}
              />
              <input data-testid="cv-country-input" value={form.country} onChange={set("country")} placeholder="Pays de résidence" className={inputCls} />
              <input data-testid="cv-city-input" value={form.city} onChange={set("city")} placeholder="Ville" className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="cv-availability-select" value={form.availability} onChange={set("availability")} className={`${inputCls} bg-white`}>
                {AVAILABILITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>Disponibilité : {o.label}</option>
                ))}
              </select>
              <select data-testid="cv-mobility-select" value={form.mobility} onChange={set("mobility")} className={`${inputCls} bg-white`}>
                {MOBILITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>Mobilité : {o.label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="cv-desired-countries-input" value={form.desired_countries} onChange={set("desired_countries")} placeholder="Pays recherchés (Canada, France…)" className={inputCls} />
              <input data-testid="cv-desired-salary-input" value={form.desired_salary} onChange={set("desired_salary")} placeholder="Salaire souhaité" className={inputCls} />
            </div>
            <select data-testid="cv-visibility-select" value={form.visibility} onChange={set("visibility")} className={`${inputCls} bg-white`}>
              <option value="public">Visibilité : public (employeurs et recruteurs)</option>
              <option value="private">Visibilité : privé (recruteurs LCS uniquement)</option>
            </select>
            <button
              data-testid="cv-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full bg-brand-turquoise text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-navy transition-colors duration-200 disabled:opacity-50"
            >
              {submitting ? "Publication…" : "Publier mon CV"}
            </button>
          </form>
        </div>

        <div className="space-y-6">
          {/* My CVs */}
          <div>
            <h2 className="font-semibold text-brand-navy mb-4">Mes CVs ({cvs.length})</h2>
            <div className="space-y-3">
              {cvs.map((cv) => (
                <div key={cv.cv_id} data-testid={`cv-card-${cv.cv_id}`} className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-brand-navy">{cv.full_name}</p>
                      <p className="text-sm text-slate-500">{cv.profession}</p>
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {[cv.city, cv.country].filter(Boolean).join(", ") || "—"} · {cv.experience_years} an(s) d'expérience
                        {cv.visibility === "private" && " · Privé"}
                      </p>
                      {cv.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {cv.skills.map((s) => (
                            <span key={s} className="text-xs bg-brand-sky text-brand-navy rounded-full px-2.5 py-0.5">{s}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      data-testid={`cv-delete-${cv.cv_id}`}
                      onClick={() => deleteCv(cv.cv_id)}
                      className="text-slate-400 hover:text-red-600 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {cvs.length === 0 && (
                <p data-testid="cvs-empty" className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-8 text-center">
                  Aucun CV publié pour le moment.
                </p>
              )}
            </div>
          </div>

          {/* Documents */}
          <DocumentsSection />

          {/* Vérifications */}
          <VerificationsSection />

          {/* My applications */}
          <div>
            <h2 className="font-semibold text-brand-navy mb-4 flex items-center gap-2">
              <Send className="w-4 h-4 text-brand-turquoise" />
              Mes candidatures ({applications.length})
            </h2>
            <div className="space-y-3">
              {applications.map((a) => (
                <div
                  key={a.application_id}
                  data-testid={`application-card-${a.application_id}`}
                  className="bg-white rounded-xl border border-slate-200 p-4 hover:border-brand-turquoise transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <Link to={`/jobs/${a.offer_id}`} className="min-w-0">
                      <p className="font-medium text-brand-navy text-sm truncate">{a.offer_title}</p>
                      <p className="text-xs text-slate-500">{a.company}</p>
                    </Link>
                    <span
                      data-testid={`application-status-${a.application_id}`}
                      className={`text-xs font-medium rounded-full px-3 py-1 shrink-0 ${
                        a.status === "refuse"
                          ? "bg-red-50 text-red-600"
                          : a.status === "embauche"
                          ? "bg-green-50 text-green-700"
                          : "bg-brand-sky text-brand-navy"
                      }`}
                    >
                      {applicationStatusLabel(a.status)}
                    </span>
                  </div>
                  <Link
                    to={`/messages/${a.application_id}`}
                    data-testid={`application-message-${a.application_id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-turquoise hover:underline mt-2"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    Messages
                  </Link>
                </div>
              ))}
              {applications.length === 0 && (
                <p data-testid="applications-empty" className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-6 text-center">
                  Aucune candidature. <Link to="/jobs" className="text-brand-turquoise font-medium hover:underline">Trouver un emploi</Link>
                </p>
              )}
            </div>
          </div>

          {/* My interviews */}
          <CandidateInterviews />

          {/* Saved offers */}
          <div>
            <h2 className="font-semibold text-brand-navy mb-4 flex items-center gap-2">
              <Bookmark className="w-4 h-4 text-brand-blue" />
              Offres sauvegardées ({savedOffers.length})
            </h2>            <div className="space-y-3">
              {savedOffers.map((o) => (
                <Link
                  key={o.offer_id}
                  to={`/jobs/${o.offer_id}`}
                  data-testid={`saved-offer-${o.offer_id}`}
                  className="block bg-white rounded-xl border border-slate-200 p-4 hover:border-brand-turquoise transition-colors"
                >
                  <p className="font-medium text-brand-navy text-sm">{o.title}</p>
                  <p className="text-xs text-slate-500">
                    {o.company} · {[o.city, o.country].filter(Boolean).join(", ")} · {employmentTypeLabel(o.employment_type)}
                  </p>
                </Link>
              ))}
              {savedOffers.length === 0 && (
                <p data-testid="saved-empty" className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-6 text-center">
                  <FileText className="w-4 h-4 inline mr-1 text-slate-300" />
                  Aucune offre sauvegardée.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
